import type { EpisodeFile } from "@/api";
import type { VideoChannelSource } from "@/components/video";

// episode-file 목록을 MultiChannelVideoPlayer 채널로 매핑한다. video kind 이고 재생 가능한
// original_url 이 인라인된 카메라 파일만 채널이 된다. label 은 camera_name 을 먼저 쓰고,
// 없으면 "Camera N"(1-based)을 쓴다. filename 은 episode 이름과 같아서(episode_000000)
// 혼란을 주므로 폴백으로 쓰지 않는다.
//
// 이 매핑은 "EpisodeFile → 재생 채널" 의 유일한 정의다. 데이터셋 상세와 리뷰 상세가 공유한다.
// 채널 해석 정책(필터, label 규칙)이 바뀌면 여기만 고친다. 그러면 두 화면에 함께 반영된다.
export function episodeFilesToChannels(files: readonly EpisodeFile[]): VideoChannelSource[] {
  return files
    .filter((file) => file.kind === "video" && file.original_url !== null)
    .map((file, index) => ({
      id: file.uuid,
      label: file.camera_name ?? `Camera ${index + 1}`,
      src: file.original_url as string,
    }));
}

/**
 * 재생 바의 "한 칸" 을 한 프레임으로 맞추는 step(초). fps 를 아는 채널이 없으면 undefined 다.
 * 호출자가 그 값을 그대로 전달하면 c6 기본 step(0.1s)을 쓴다.
 *
 * 채널마다 fps 가 다르면 **가장 낮은 fps** 를 쓴다. 프레임 간격이 가장 긴 채널에 맞춰야
 * 한 번 눌렀을 때 모든 채널이 최소 한 프레임 이동한다. 높은 fps 에 맞추면 느린 채널의 화면은
 * 바뀌지 않는다. 카탈로그는 fps 를 dataset 단일값으로 다룬다. 그래서 값이 실제로 다른 경우는
 * 값이 일부만 채워진 옛 데이터셋뿐이다.
 *
 * 채널이 되는 파일(재생 가능한 video)만 센다. 화면에 없는 파일의 fps 로 step 을 정하면
 * 화면에 보이는 영상과 맞지 않는다.
 */
export function episodeFilesStepSeconds(files: readonly EpisodeFile[]): number | undefined {
  const rates: number[] = [];
  for (const file of files) {
    // fps 는 `number | null` 이지만, 값이 유효한지도 여기서 확인한다. 0 이나 유한하지 않은 값으로
    // 나누면 step 이 Infinity/NaN 이 된다. 그러면 재생 바 전체가 동작하지 않는다.
    if (file.kind === "video" && file.original_url !== null && isUsableFps(file.fps)) {
      rates.push(file.fps);
    }
  }
  if (rates.length === 0) {
    return undefined;
  }
  return 1 / Math.min(...rates);
}

function isUsableFps(fps: number | null): fps is number {
  return fps !== null && Number.isFinite(fps) && fps > 0;
}
